/**
 * @file useShopFontsState.ts
 * @description Состояние своих шрифтов магазина для контекста
 * (`ShopFontsContext`, phase19.16 Р1-7; SB99): список с сервера, реестр
 * `createSiteFontRegistry` и действия.
 *
 * Одна истина — сервер: после любой правки список перечитывается, а не
 * правится руками. Удаление может перевести роли черновика на шрифт по
 * умолчанию — тогда вызывающий перечитывает оформление черновика
 * (`onThemeChanged`).
 */

import { createSiteFontRegistry, SITE_LIMITS } from "@chaiberi/shared";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ShopFontsApi } from "../../components/site-view/shop-fonts-context";
import { siteFonts, type AddShopFontFileInput, type CreateShopFontInput } from "../../lib/api-site-view";
import { fontListErrorText } from "../../lib/site-view/shop-font-errors";
import { toShopFontEntry, type ShopFontDto } from "../../lib/site-view/shop-fonts";

interface Options {
  /** Грузить список (есть право настроек и доступ к виду сайта). */
  enabled: boolean;
  canManage: boolean;
  /** Сервер изменил оформление черновика (удалён выбранный шрифт). */
  onThemeChanged?: () => Promise<void> | void;
}

export function useShopFontsState({ enabled, canManage, onThemeChanged }: Options): ShopFontsApi {
  const [items, setItems] = useState<ShopFontDto[]>([]);
  const [limit, setLimit] = useState<number>(SITE_LIMITS.shopFonts);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const themeChanged = useRef(onThemeChanged);
  themeChanged.current = onThemeChanged;

  const reload = useCallback(async () => {
    try {
      const list = await siteFonts.list();
      setItems(list.items);
      setLimit(list.limit);
      setLoaded(true);
      setError(null);
    } catch (err) {
      // Любой сбой (не-2xx, пустое тело, нет связи, исключение) завершает загрузку ошибкой.
      setError(fontListErrorText(err));
    }
  }, []);

  useEffect(() => {
    if (enabled) void reload();
  }, [enabled, reload]);

  const create = useCallback(
    async (input: CreateShopFontInput) => {
      const font = await siteFonts.create(input);
      await reload();
      return font;
    },
    [reload],
  );
  const addFile = useCallback(
    async (id: string, input: AddShopFontFileInput) => {
      const font = await siteFonts.addFile(id, input);
      await reload();
      return font;
    },
    [reload],
  );
  const rename = useCallback(
    async (id: string, family: string) => {
      await siteFonts.update(id, { family });
      await reload();
    },
    [reload],
  );
  const remove = useCallback(
    async (id: string) => {
      const result = await siteFonts.remove(id);
      await reload();
      // Шрифт стоял в черновике — сервер перевёл эти роли на `system` и поднял
      // версию THEME. Поля нет — черновик не менялся, несохранённое не трогаем.
      if (result.draftRoles?.length) await themeChanged.current?.();
      return result;
    },
    [reload],
  );

  const registry = useMemo(() => createSiteFontRegistry(items.map(toShopFontEntry)), [items]);

  return useMemo(
    () => ({
      items,
      loaded,
      error,
      limit,
      canManage,
      registry,
      reload,
      inspect: siteFonts.inspect,
      create,
      addFile,
      rename,
      remove,
    }),
    [items, loaded, error, limit, canManage, registry, reload, create, addFile, rename, remove],
  );
}
